import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import { Section965Service } from '../../section965.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

export interface FilingGroupOption {
  FILING_GROUP?: string;
  GROUP_NAME?: string;
}

/**
 * Section 965 Filing Group modal.
 * Migrated from section-965-fg-tpl.html + Section965FgController.
 */
@Component({
    selector: 'gtw-section-965-fg',
    templateUrl: './section-965-fg.component.html',
    styleUrls: ['./section-965-fg.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Section965FgComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  modalTitle = 'Section 965 Filing Group';

  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};

  filingGroups: { list: FilingGroupOption[]; selected: FilingGroupOption | null } = {
    list: [],
    selected: null
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private section965Service: Section965Service,
    private gtwSnackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.loadFilingGroups();
    this.cdr.detectChanges();
  }

  private loadFilingGroups(): void {
    this.section965Service.getFilingGroups(
      this.GENERAL_CONFIG.base_url,
      this.rowData.FILING_GROUP ?? '',
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list: FilingGroupOption[]) => {
        this.filingGroups.list = list || [];
        if (this.rowData.NEW_FILING_GROUP) {
          const idx = this.filingGroups.list.findIndex((s) => s.FILING_GROUP === this.rowData.NEW_FILING_GROUP);
          this.filingGroups.selected = idx >= 0 ? this.filingGroups.list[idx] : this.filingGroups.list[0] ?? null;
        } else {
          this.filingGroups.selected = this.filingGroups.list[0] ?? null;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cdr.detectChanges();
      }
    });
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--orange', true, 4);
      return;
    }
    if (!this.filingGroups.selected?.FILING_GROUP) {
      this.gtwSnackbar.openSnackBar('Please select a filing group', 'gtw-snackbar--yellow', true, 4);
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const params = {
      ghost_scenario: this.rowData.GHOST_SCENARIO ?? '',
      leid: this.rowData.LEID ?? '',
      tax_code: this.rowData.CDR_NO ?? '',
      filing_group: this.rowData.FILING_GROUP ?? '',
      new_filing_group: this.filingGroups.selected.FILING_GROUP
    };

    this.section965Service.saveFilingGroup(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(result?.errorMessage || 'Save failed', 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar('Filing Group has been updated', 'gtw-snackbar--green', true, 4);
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    this.loadFilingGroups();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  compareFilingGroup(a: FilingGroupOption | null, b: FilingGroupOption | null): boolean {
    if (!a || !b) {
      return a === b;
    }
    return (a.FILING_GROUP ?? '') === (b.FILING_GROUP ?? '');
  }
}
